import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { nanoid } from '../utils/nanoid.js'
import { EditorStore, Project, Snapshot, ChatSession, ChatMessage } from './types.js'

export const useEditorStore = create<EditorStore>()(
  devtools(
    persist(
      (set, get) => ({
        markup: '',
        parseErrors: [],
        selectedNodeId: null,
        hoveredNodeId: null,
        selectedNodeHtml: null,
        viewMode: 'edit',
        theme: 'light',
        zoom: 100,
        projects: [],
        currentProject: null,
        isHtmlEditorExpanded: false,
        isAIChatVisible: false,
        htmlEditorHeight: 300,
        rightSidebarTab: 'chat',
        isAIProcessing: false,
        chatInput: '',
        setMarkup: markup => {
          const state = get()
          if (state.currentProject) {
            const updatedProject = {
              ...state.currentProject,
              markup,
              updatedAt: Date.now()
            }
            set({
              markup,
              currentProject: updatedProject,
              projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
            })
          } else {
            set({ markup })
          }
        },
        buildTreeFromMarkup: markup => {
          // We don't need to parse or build a tree anymore
          // Just validate that it's valid HTML and update the markup
          set({
            markup,
            parseErrors: []
          })
        },
    selectNode: id => {
      set({ selectedNodeId: id })
    },
    hoverNode: id => {
      set({ hoveredNodeId: id })
    },
    setSelectedNodeHtml: html => {
      set({ selectedNodeHtml: html })
    },
    setViewMode: mode => set({ viewMode: mode }),
    setZoom: zoom => set({ zoom: Math.max(10, Math.min(500, zoom)) }),
    setTheme: theme => {
      set({ theme })
      // Apply theme to preview canvas
      const canvas = document.getElementById('wireframelm-canvas-root')
      if (canvas) {
        if (theme === 'dark') {
          canvas.classList.add('dark')
        } else {
          canvas.classList.remove('dark')
        }
      }
    },
    createProject: name => {
      const initialChatSession: ChatSession = {
        id: nanoid(),
        name: 'Chat 1',
        messages: [],
        createdAt: Date.now(),
        updatedAt: Date.now()
      }
      const project: Project = {
        id: nanoid(),
        name,
        markup: '<div class="flex min-h-screen p-6">\n  <!-- Your content here -->\n</div>',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        chatSessions: [initialChatSession],
        currentChatSessionId: initialChatSession.id,
        snapshots: [],
        currentSnapshotIndex: -1
      }
      set(state => ({
        projects: [project, ...state.projects],
        currentProject: project,
        markup: project.markup
      }))
    },
    loadProject: id => {
      const state = get()
      const project = state.projects.find(p => p.id === id)
      if (project) {
        set({ currentProject: project, markup: project.markup })
      }
    },
    saveProject: () => {
      const state = get()
      if (!state.currentProject) {
        return
      }
      const updatedProject = {
        ...state.currentProject,
        markup: state.markup,
        updatedAt: Date.now()
      }
      set(state => ({
        currentProject: updatedProject,
        projects: state.projects.map(p => (p.id === updatedProject.id ? updatedProject : p))
      }))
    },
    deleteProject: id => {
      set(state => {
        const newProjects = state.projects.filter(p => p.id !== id)
        const newCurrentProject = state.currentProject?.id === id ? null : state.currentProject
        return {
          projects: newProjects,
          currentProject: newCurrentProject,
          markup: newCurrentProject?.markup ?? ''
        }
      })
    },
    loadExampleAsProject: (name, markup) => {
      const initialChatSession: ChatSession = {
        id: nanoid(),
        name: 'Chat 1',
        messages: [],
        createdAt: Date.now(),
        updatedAt: Date.now()
      }
      const project: Project = {
        id: nanoid(),
        name: `Example: ${name}`,
        markup,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        chatSessions: [initialChatSession],
        currentChatSessionId: initialChatSession.id,
        snapshots: [],
        currentSnapshotIndex: -1
      }
      set(state => ({
        projects: [project, ...state.projects],
        currentProject: project,
        markup: project.markup
      }))
    },
    toggleHtmlEditor: () => set(state => ({ isHtmlEditorExpanded: !state.isHtmlEditorExpanded })),
    toggleAIChat: () => set(state => ({
      isAIChatVisible: !state.isAIChatVisible,
      rightSidebarTab: !state.isAIChatVisible ? 'chat' : state.rightSidebarTab
    })),
    setHtmlEditorHeight: height => set({ htmlEditorHeight: height }),
    setRightSidebarTab: tab => set({ rightSidebarTab: tab }),
    // Snapshot management
    createSnapshot: (userPrompt, aiSummary) => {
      const state = get()
      if (!state.currentProject) {
        console.warn('Cannot create snapshot without a current project')
        return
      }

      // Ensure snapshots array exists (for migration edge cases)
      const snapshots = state.currentProject.snapshots ?? []
      const currentSnapshotIndex = state.currentProject.currentSnapshotIndex ?? -1

      // Don't create duplicate snapshots if markup hasn't changed
      const currentSnapshot = snapshots[currentSnapshotIndex]
      if (currentSnapshot && currentSnapshot.markup === state.markup) {
        return
      }

      const snapshot: Snapshot = {
        id: nanoid(),
        timestamp: Date.now(),
        markup: state.markup
      }
      if (userPrompt !== undefined) {
        snapshot.userPrompt = userPrompt
      }
      if (aiSummary !== undefined) {
        snapshot.aiSummary = aiSummary
      }
      const parentSnapshot = snapshots[currentSnapshotIndex]
      if (parentSnapshot?.id) {
        snapshot.parentSnapshotId = parentSnapshot.id
      }
      // Remove any snapshots after the current index (redo history)
      const newSnapshots = snapshots.slice(0, currentSnapshotIndex + 1)

      const updatedProject = {
        ...state.currentProject,
        snapshots: [...newSnapshots, snapshot],
        currentSnapshotIndex: newSnapshots.length,
        updatedAt: Date.now()
      }

      set(state => ({
        currentProject: updatedProject,
        projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
      }))
    },
    restoreSnapshot: snapshotId => {
      const state = get()
      if (!state.currentProject) {
        return
      }

      const snapshots = state.currentProject.snapshots ?? []
      const index = snapshots.findIndex(s => s.id === snapshotId)
      if (index !== -1) {
        const snapshot = snapshots[index]
        if (snapshot) {
          const updatedProject = {
            ...state.currentProject,
            snapshots,
            currentSnapshotIndex: index,
            markup: snapshot.markup,
            updatedAt: Date.now()
          }

          set(state => ({
            markup: snapshot.markup,
            currentProject: updatedProject,
            projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
          }))
        }
      }
    },
    undo: () => {
      const state = get()
      if (!state.currentProject) {
        return
      }

      const snapshots = state.currentProject.snapshots ?? []
      const currentSnapshotIndex = state.currentProject.currentSnapshotIndex ?? -1

      if (currentSnapshotIndex <= 0) {
        return
      }

      const newIndex = currentSnapshotIndex - 1
      const snapshot = snapshots[newIndex]
      if (snapshot) {
        const updatedProject = {
          ...state.currentProject,
          snapshots,
          currentSnapshotIndex: newIndex,
          markup: snapshot.markup,
          updatedAt: Date.now()
        }

        set(state => ({
          markup: snapshot.markup,
          currentProject: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        }))
      }
    },
    redo: () => {
      const state = get()
      if (!state.currentProject) {
        return
      }

      const snapshots = state.currentProject.snapshots ?? []
      const currentSnapshotIndex = state.currentProject.currentSnapshotIndex ?? -1

      if (currentSnapshotIndex >= snapshots.length - 1) {
        return
      }

      const newIndex = currentSnapshotIndex + 1
      const snapshot = snapshots[newIndex]
      if (snapshot) {
        const updatedProject = {
          ...state.currentProject,
          snapshots,
          currentSnapshotIndex: newIndex,
          markup: snapshot.markup,
          updatedAt: Date.now()
        }

        set(state => ({
          markup: snapshot.markup,
          currentProject: updatedProject,
          projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
        }))
      }
    },
    canUndo: () => {
      const state = get()
      if (!state.currentProject) {
        return false
      }
      const currentSnapshotIndex = state.currentProject.currentSnapshotIndex ?? -1
      return currentSnapshotIndex > 0
    },
    canRedo: () => {
      const state = get()
      if (!state.currentProject) {
        return false
      }
      const snapshots = state.currentProject.snapshots ?? []
      const currentSnapshotIndex = state.currentProject.currentSnapshotIndex ?? -1
      return currentSnapshotIndex < snapshots.length - 1
    },
    // AI Chat
    setAIProcessing: processing => set({ isAIProcessing: processing }),
    setChatInput: input => set({ chatInput: input }),

    // Chat session management
    createChatSession: (name) => {
      const state = get()
      if (!state.currentProject) {
        console.warn('Cannot create chat session without a current project')
        return
      }

      const chatCount = state.currentProject.chatSessions.length
      const newSession: ChatSession = {
        id: nanoid(),
        name: name ?? `Chat ${chatCount + 1}`,
        messages: [],
        createdAt: Date.now(),
        updatedAt: Date.now()
      }

      const updatedProject = {
        ...state.currentProject,
        chatSessions: [...state.currentProject.chatSessions, newSession],
        currentChatSessionId: newSession.id,
        updatedAt: Date.now()
      }

      set(state => ({
        currentProject: updatedProject,
        projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
      }))
    },

    deleteChatSession: (sessionId) => {
      const state = get()
      if (!state.currentProject) {
        return
      }

      const remainingSessions = state.currentProject.chatSessions.filter(s => s.id !== sessionId)

      // Don't allow deleting the last session
      if (remainingSessions.length === 0) {
        console.warn('Cannot delete the last chat session')
        return
      }

      // If we're deleting the current session, switch to the first remaining session
      const newCurrentSessionId = state.currentProject.currentChatSessionId === sessionId
        ? remainingSessions[0]?.id ?? null
        : state.currentProject.currentChatSessionId

      const updatedProject = {
        ...state.currentProject,
        chatSessions: remainingSessions,
        currentChatSessionId: newCurrentSessionId,
        updatedAt: Date.now()
      }

      set(state => ({
        currentProject: updatedProject,
        projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
      }))
    },

    switchChatSession: (sessionId) => {
      const state = get()
      if (!state.currentProject) {
        return
      }

      const session = state.currentProject.chatSessions.find(s => s.id === sessionId)
      if (!session) {
        console.warn(`Chat session ${sessionId} not found`)
        return
      }

      const updatedProject = {
        ...state.currentProject,
        currentChatSessionId: sessionId,
        updatedAt: Date.now()
      }

      set(state => ({
        currentProject: updatedProject,
        projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
      }))
    },

    addChatMessage: (message) => {
      const state = get()
      if (!state.currentProject || !state.currentProject.currentChatSessionId) {
        console.warn('Cannot add chat message without a current project and chat session')
        return
      }

      const updatedProject = {
        ...state.currentProject,
        chatSessions: state.currentProject.chatSessions.map(session =>
          session.id === state.currentProject!.currentChatSessionId
            ? { ...session, messages: [...session.messages, message], updatedAt: Date.now() }
            : session
        ),
        updatedAt: Date.now()
      }

      set(state => ({
        currentProject: updatedProject,
        projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
      }))
    },

    clearCurrentChat: () => {
      const state = get()
      if (!state.currentProject || !state.currentProject.currentChatSessionId) {
        return
      }

      const updatedProject = {
        ...state.currentProject,
        chatSessions: state.currentProject.chatSessions.map(session =>
          session.id === state.currentProject!.currentChatSessionId
            ? { ...session, messages: [], updatedAt: Date.now() }
            : session
        ),
        updatedAt: Date.now()
      }

      set(state => ({
        currentProject: updatedProject,
        projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p)
      }))
    },

    getCurrentChatMessages: () => {
      const state = get()
      if (!state.currentProject || !state.currentProject.currentChatSessionId) {
        return []
      }

      const currentSession = state.currentProject.chatSessions.find(
        s => s.id === state.currentProject!.currentChatSessionId
      )

      return currentSession?.messages ?? []
    }
      }),
      {
        name: 'wireframelm-editor-storage',
        version: 1,
        partialize: state => ({
          projects: state.projects,
          currentProject: state.currentProject,
          markup: state.currentProject?.markup ?? state.markup,
          isHtmlEditorExpanded: state.isHtmlEditorExpanded,
          htmlEditorHeight: state.htmlEditorHeight,
          theme: state.theme,
          rightSidebarTab: state.rightSidebarTab
        }),
        migrate: (persistedState: any, version: number) => {
          // Migrate old projects to include chat sessions and per-project snapshots
          if (version === 0) {
            const state = persistedState as any

            // Move global snapshots to projects if they exist
            const globalSnapshots = state.snapshots || []
            const globalSnapshotIndex = state.currentSnapshotIndex ?? -1

            if (state.projects) {
              state.projects = state.projects.map((project: any) => {
                const updatedProject = { ...project }

                // Add chat sessions if missing
                if (!updatedProject.chatSessions) {
                  const initialChatSession: ChatSession = {
                    id: nanoid(),
                    name: 'Chat 1',
                    messages: [],
                    createdAt: Date.now(),
                    updatedAt: Date.now()
                  }
                  updatedProject.chatSessions = [initialChatSession]
                  updatedProject.currentChatSessionId = initialChatSession.id
                }

                // Add snapshots if missing (use global snapshots for current project)
                if (!updatedProject.snapshots) {
                  if (state.currentProject?.id === project.id && globalSnapshots.length > 0) {
                    updatedProject.snapshots = globalSnapshots
                    updatedProject.currentSnapshotIndex = globalSnapshotIndex
                  } else {
                    updatedProject.snapshots = []
                    updatedProject.currentSnapshotIndex = -1
                  }
                }

                return updatedProject
              })
            }

            if (state.currentProject) {
              if (!state.currentProject.chatSessions) {
                const initialChatSession: ChatSession = {
                  id: nanoid(),
                  name: 'Chat 1',
                  messages: [],
                  createdAt: Date.now(),
                  updatedAt: Date.now()
                }
                state.currentProject.chatSessions = [initialChatSession]
                state.currentProject.currentChatSessionId = initialChatSession.id
              }

              if (!state.currentProject.snapshots) {
                state.currentProject.snapshots = globalSnapshots
                state.currentProject.currentSnapshotIndex = globalSnapshotIndex
              }
            }

            // Remove old global snapshots from state
            delete state.snapshots
            delete state.currentSnapshotIndex
          }
          return persistedState
        }
      }
    )
  )
)

export function useEditorSelection() {
  return useEditorStore(state => ({
    selectedNodeId: state.selectedNodeId,
    selectNode: state.selectNode
  }))
}
